import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { getBundleValueUp } from 'lib/bundleAndSave/getBundleValueUp'

import { sum, unique } from 'lib/array/arrayUtils'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import { ViewOfferButton } from './ViewOfferButton'

interface Props {
  offer: App.BundleOffer | App.BundleOfferSummary
  pkg: App.BundlePackageWithPrice
  variant: 'default' | 'condensed'
}

function BundleAndSavePriceDetails(props: Props) {
  const { offer, pkg, variant } = props
  const intl = useIntl()

  const {
    saleUnit,
    totalPrice,
    totalValue,
    valueOffPercent,
  } = useOfferPackagePrice(pkg, offer)

  const { shouldShowDiscountPercent, shouldShowValue } = getBundleValueUp(offer, pkg)

  const isDiscountPillHidden = offer.isDiscountPillHidden

  const duration = useMemo(() => {
    return sum(pkg.packages, (p) => p.duration)
  }, [pkg.packages])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
      <VerticalSpacer gap={8}>
        <Group direction="vertical">
          <PriceRowPriceCaption>
            {intl.formatMessage(
              {
                id: '2fxkzVq5WAzjvNA-yL0G',
                defaultMessage: '{count, plural, one {# night} other {# nights}}, 2 hotels from',
                description: 'Duration in nights with 2 hotels for bundle and save offer card pricing',
              },
              { count: duration },
            )}
          </PriceRowPriceCaption>
          <PriceRowPrice
            size="L"
            price={Math.ceil(totalPrice)}
            saleUnit={saleUnit}
          />
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            size="L"
            originalValue={Math.ceil(totalValue)}
            discountPercentage={shouldShowDiscountPercent && !isDiscountPillHidden ? valueOffPercent : undefined}
          />}
          {variant === 'default' && <PriceRowTaxesAndFees />}
        </Group>
        <PriceRowAgentHubCommission
          productType={offer.productType}
          offerId={offer.id}
          offerLocations={offerLocations}
          vendorName={vendorName}
        />
      </VerticalSpacer>
      <CSSBreakpoint min="desktop">
        <ViewOfferButton variant={variant} />
      </CSSBreakpoint>
    </Group>
  )
}

export default BundleAndSavePriceDetails
